<template>
  <div>
    <!-- Tabs для різних розділів меню -->
    <b-tabs content-class="mt-3" justified @activate-tab="onTabActivated">
      <!-- Супер-колекції -->
      <b-tab :title="$t('GlovoSuperCollections')" active>
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">{{ $t("GlovoSuperCollections") }}</h5>
            <div>
              <b-button
                variant="danger"
                size="sm"
                class="me-2"
                @click="showClearAllModal = true"
              >
                <i class="ri-delete-bin-line"></i>
                {{ $t("GlovoClearAll") }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                @click="loadSuperCollections"
                :disabled="loadingSuperCollections"
              >
                <i class="ri-refresh-line"></i>
                {{ $t("Refresh") }}
              </b-button>
            </div>
          </div>

          <div v-if="loadingSuperCollections" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ $t("Loading") }}...</span>
            </div>
          </div>

          <div
            v-else-if="superCollections.length === 0"
            class="text-center text-muted py-4"
          >
            {{ $t("GlovoNoSuperCollections") }}
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{{ $t("Name") }}</th>
                  <th>{{ $t("GlovoCollectionsCount") }}</th>
                  <th>{{ $t("Status") }}</th>
                  <th class="text-end">{{ $t("Actions") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sc in superCollections" :key="sc.id">
                  <td>{{ sc.id }}</td>
                  <td>
                    <strong>{{ sc.title }}</strong>
                    <br />
                    <small class="text-muted" v-if="sc.image">{{
                      sc.image
                    }}</small>
                  </td>
                  <td>
                    <span class="badge bg-info">{{
                      sc.collections?.length || 0
                    }}</span>
                  </td>
                  <td>
                    <span
                      :class="[
                        'badge',
                        sc.active ? 'bg-success' : 'bg-secondary',
                      ]"
                    >
                      {{ sc.active ? $t("Available") : $t("Unavailable") }}
                    </span>
                  </td>
                  <td class="text-end">
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="confirmDelete('superCollection', sc)"
                    >
                      <i class="ri-delete-bin-line"></i>
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>
      </b-tab>

      <!-- Колекції -->
      <b-tab :title="$t('GlovoCollections')">
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">{{ $t("GlovoCollections") }}</h5>
            <b-button
              variant="primary"
              size="sm"
              @click="loadCollections"
              :disabled="loadingCollections"
            >
              <i class="ri-refresh-line"></i>
              {{ $t("Refresh") }}
            </b-button>
          </div>

          <div v-if="loadingCollections" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ $t("Loading") }}...</span>
            </div>
          </div>

          <div
            v-else-if="collections.length === 0"
            class="text-center text-muted py-4"
          >
            {{ $t("GlovoNoCollections") }}
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{{ $t("Name") }}</th>
                  <th>{{ $t("GlovoProductsCount") }}</th>
                  <th>{{ $t("Status") }}</th>
                  <th class="text-end">{{ $t("Actions") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="col in collections" :key="col.id">
                  <td>{{ col.id }}</td>
                  <td>
                    <strong>{{ col.title }}</strong>
                    <br />
                    <small class="text-muted" v-if="col.image">{{
                      col.image
                    }}</small>
                  </td>
                  <td>
                    <span class="badge bg-info">{{
                      col.productGroups?.length || 0
                    }}</span>
                  </td>
                  <td>
                    <span
                      :class="[
                        'badge',
                        col.active ? 'bg-success' : 'bg-secondary',
                      ]"
                    >
                      {{ col.active ? $t("Available") : $t("Unavailable") }}
                    </span>
                  </td>
                  <td class="text-end">
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="confirmDelete('collection', col)"
                    >
                      <i class="ri-delete-bin-line"></i>
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>
      </b-tab>

      <!-- Товари -->
      <b-tab :title="$t('GlovoProducts')">
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">{{ $t("GlovoProducts") }}</h5>
            <div>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="me-2"
                @click="showProductSearch = !showProductSearch"
              >
                <i class="ri-search-line"></i>
                {{ $t("Search") }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                @click="loadProducts"
                :disabled="loadingProducts"
              >
                <i class="ri-refresh-line"></i>
                {{ $t("Refresh") }}
              </b-button>
            </div>
          </div>

          <!-- Пошук -->
          <div v-if="showProductSearch" class="mb-3">
            <b-form-input
              v-model="productSearchQuery"
              :placeholder="$t('GlovoSearchProducts')"
              @input="filterProducts"
            ></b-form-input>
          </div>

          <div v-if="loadingProducts" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ $t("Loading") }}...</span>
            </div>
          </div>

          <div
            v-else-if="filteredProducts.length === 0"
            class="text-center text-muted py-4"
          >
            {{ $t("GlovoNoProducts") }}
          </div>

          <div v-else>
            <!-- Відображаємо товари по групах -->
            <div
              v-for="group in paginatedProducts"
              :key="group.id"
              class="mb-4"
            >
              <div
                class="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded"
              >
                <h6 class="mb-0">
                  <strong>{{ group.title }}</strong>
                  <span class="badge bg-info ms-2"
                    >{{ group.items?.length || 0 }} товарів</span
                  >
                </h6>
                <div class="d-flex align-items-center gap-2">
                  <span
                    :class="[
                      'badge',
                      group.active ? 'bg-success' : 'bg-secondary',
                    ]"
                  >
                    {{ group.active ? $t("Available") : $t("Unavailable") }}
                  </span>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="confirmDelete('productGroup', group)"
                    :title="$t('GlovoDeleteProductGroup')"
                  >
                    <i class="ri-delete-bin-line"></i>
                  </b-button>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>{{ $t("Name") }}</th>
                      <th>{{ $t("Price") }}</th>
                      <th>{{ $t("Status") }}</th>
                      <th class="text-end">{{ $t("Actions") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in group.items" :key="item.id">
                      <td>{{ item.id }}</td>
                      <td>
                        <div class="d-flex align-items-center">
                          <img
                            v-if="item.image"
                            :src="item.image"
                            alt=""
                            style="
                              width: 40px;
                              height: 40px;
                              object-fit: cover;
                              border-radius: 4px;
                            "
                            class="me-2"
                          />
                          <div>
                            <strong>{{ item.title }}</strong>
                            <br />
                            <small class="text-muted">{{
                              item.description
                            }}</small>
                          </div>
                        </div>
                      </td>
                      <td>{{ formatPrice(item.price) }}</td>
                      <td>
                        <span
                          :class="[
                            'badge',
                            item.active ? 'bg-success' : 'bg-secondary',
                          ]"
                        >
                          {{
                            item.active ? $t("Available") : $t("Unavailable")
                          }}
                        </span>
                      </td>
                      <td class="text-end">
                        <b-button
                          variant="outline-danger"
                          size="sm"
                          @click="confirmDelete('product', item, group.id)"
                        >
                          <i class="ri-delete-bin-line"></i>
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Пагінація -->
            <div
              v-if="filteredProducts.length > productsPerPage"
              class="d-flex justify-content-center mt-3"
            >
              <b-pagination
                v-model="currentProductPage"
                :total-rows="filteredProducts.length"
                :per-page="productsPerPage"
                aria-controls="products-table"
              ></b-pagination>
            </div>
          </div>
        </b-card>
      </b-tab>

      <!-- Опції -->
      <b-tab :title="$t('GlovoOptions')">
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">{{ $t("GlovoOptions") }}</h5>
            <b-button
              variant="primary"
              size="sm"
              @click="loadOptions"
              :disabled="loadingOptions"
            >
              <i class="ri-refresh-line"></i>
              {{ $t("Refresh") }}
            </b-button>
          </div>

          <div v-if="loadingOptions" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ $t("Loading") }}...</span>
            </div>
          </div>

          <div
            v-else-if="optionGroups.length === 0"
            class="text-center text-muted py-4"
          >
            {{ $t("GlovoNoOptions") }}
          </div>

          <div v-else>
            <b-card
              v-for="group in optionGroups"
              :key="group.id"
              class="mb-3"
              border-variant="light"
            >
              <div
                class="d-flex justify-content-between align-items-start mb-3"
              >
                <div>
                  <h6 class="mb-1">
                    <strong>{{ group.title }}</strong>
                  </h6>
                  <div class="mb-2">
                    <span class="badge bg-info me-2">
                      {{ $t("GlovoOptionsCount") }}:
                      {{ group.items?.length || 0 }}
                    </span>
                    <span class="badge bg-secondary me-2">
                      Min: {{ group.min }} | Max: {{ group.max }}
                    </span>
                    <span
                      :class="[
                        'badge',
                        group.active ? 'bg-success' : 'bg-secondary',
                      ]"
                    >
                      {{ group.active ? $t("Available") : $t("Unavailable") }}
                    </span>
                  </div>
                </div>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="confirmDelete('optionGroup', group)"
                >
                  <i class="ri-delete-bin-line"></i>
                </b-button>
              </div>

              <!-- Опції в групі -->
              <div v-if="group.items && group.items.length > 0" class="mt-3">
                <table class="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>{{ $t("Name") }}</th>
                      <th>{{ $t("Price") }}</th>
                      <th>{{ $t("Status") }}</th>
                      <th class="text-end">{{ $t("Actions") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="option in group.items" :key="option.id">
                      <td>{{ option.id }}</td>
                      <td>
                        <strong>{{ option.title }}</strong>
                      </td>
                      <td>{{ formatPrice(option.price) }}</td>
                      <td>
                        <span
                          :class="[
                            'badge',
                            option.active ? 'bg-success' : 'bg-secondary',
                          ]"
                        >
                          {{
                            option.active ? $t("Available") : $t("Unavailable")
                          }}
                        </span>
                      </td>
                      <td class="text-end">
                        <b-button
                          variant="outline-danger"
                          size="sm"
                          @click="confirmDelete('option', option, group.id)"
                        >
                          <i class="ri-delete-bin-line"></i>
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-card>
          </div>
        </b-card>
      </b-tab>
    </b-tabs>

    <!-- Modal підтвердження видалення -->
    <b-modal
      v-model="showDeleteModal"
      :title="$t('ConfirmDeletion')"
      @ok="deleteItem"
      :ok-title="$t('Delete')"
      :cancel-title="$t('Cancel')"
      ok-variant="danger"
    >
      <p>{{ deleteModalMessage }}</p>
      <div v-if="itemToDelete" class="alert alert-warning">
        <p class="mb-1">
          <strong>{{ $t("Name") }}:</strong>
          {{ itemToDelete.title || itemToDelete.name }}
        </p>
        <p class="mb-0"><strong>ID:</strong> {{ itemToDelete.id }}</p>
      </div>
    </b-modal>

    <!-- Modal очищення всього меню -->
    <b-modal
      v-model="showClearAllModal"
      :title="$t('GlovoClearAllMenu')"
      @ok="clearAllMenu"
      :ok-title="$t('GlovoClearAll')"
      :cancel-title="$t('Cancel')"
      ok-variant="danger"
      :ok-disabled="clearAllConfirmWord !== 'data'"
    >
      <div class="alert alert-danger">
        <i class="ri-alert-line me-2"></i>
        <strong>{{ $t("Warning") }}!</strong> {{ $t("GlovoClearAllWarning") }}
      </div>

      <p>{{ $t("GlovoClearAllDescription") }}</p>

      <b-form-group
        :label="$t('GlovoClearAllConfirmLabel')"
        label-for="clear-confirm-input"
      >
        <b-form-input
          id="clear-confirm-input"
          v-model="clearAllConfirmWord"
          :placeholder="$t('GlovoClearAllPlaceholder')"
          trim
        ></b-form-input>
        <small class="text-muted">{{ $t("GlovoClearAllHint") }}</small>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  try_GetCollections,
  try_GetSuperCollections,
  try_GetProducts,
  try_GetOptions,
  try_DeleteCollection,
  try_DeleteSuperCollection,
  try_DeleteProduct,
  try_DeleteProductGroup,
  try_DeleteOptionGroup,
  try_DeleteOption,
  try_ClearAllMenu,
} from "@/api/services/glovoMenu";

export default {
  props: ["storeAddressId", "environment"],
  data() {
    return {
      // Super Collections
      superCollections: [],
      loadingSuperCollections: false,
      superCollectionsLoaded: false,

      // Collections
      collections: [],
      loadingCollections: false,
      collectionsLoaded: false,

      // Products
      products: [],
      filteredProducts: [],
      loadingProducts: false,
      productsLoaded: false,
      showProductSearch: false,
      productSearchQuery: "",
      currentProductPage: 1,
      productsPerPage: 20,

      // Options
      optionGroups: [],
      loadingOptions: false,
      optionsLoaded: false,

      // Delete modal
      showDeleteModal: false,
      itemToDelete: null,
      deleteType: null,
      deleteGroupId: null,
      deleteModalMessage: "",

      // Clear all modal
      showClearAllModal: false,
      clearAllConfirmWord: "",

      // Current active tab
      activeTabIndex: 0,
    };
  },
  computed: {
    paginatedProducts() {
      const start = (this.currentProductPage - 1) * this.productsPerPage;
      const end = start + this.productsPerPage;
      return this.filteredProducts.slice(start, end);
    },
  },
  watch: {
    storeAddressId: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          // Скидаємо флаги завантаження при зміні магазину
          this.superCollectionsLoaded = false;
          this.collectionsLoaded = false;
          this.productsLoaded = false;
          this.optionsLoaded = false;

          // Завантажуємо дані тільки для активного табу
          this.loadDataForActiveTab();
        }
      },
    },
    environment: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // Імпортуємо функцію setEnvironment та встановлюємо середовище
          import("@/api/endpoints/glovoMenu").then((module) => {
            module.setEnvironment(newVal);

            // Скидаємо всі завантажені дані при зміні середовища
            this.superCollectionsLoaded = false;
            this.collectionsLoaded = false;
            this.productsLoaded = false;
            this.optionsLoaded = false;

            // Перезавантажуємо дані для активного табу
            if (this.storeAddressId) {
              this.loadDataForActiveTab();
            }
          });
        }
      },
    },
  },
  methods: {
    onTabActivated(newTabIndex) {
      this.activeTabIndex = newTabIndex;
      this.loadDataForActiveTab();
    },

    loadDataForActiveTab() {
      if (!this.storeAddressId) return;

      switch (this.activeTabIndex) {
        case 0: // Супер-колекції
          if (!this.superCollectionsLoaded) {
            this.loadSuperCollections();
          }
          break;
        case 1: // Колекції
          if (!this.collectionsLoaded) {
            this.loadCollections();
          }
          break;
        case 2: // Товари
          if (!this.productsLoaded) {
            this.loadProducts();
          }
          break;
        case 3: // Опції
          if (!this.optionsLoaded) {
            this.loadOptions();
          }
          break;
      }
    },

    async loadSuperCollections() {
      if (!this.storeAddressId) return;

      try {
        this.loadingSuperCollections = true;
        const response = await try_GetSuperCollections(this.storeAddressId);
        // Відповідь може бути масивом або об'єктом з data
        const data = Array.isArray(response) ? response : response?.data || [];
        this.superCollections = data;
        this.superCollectionsLoaded = true;
      } catch (error) {
        console.error("Error loading super collections:", error);
        this.$toast.error(this.$t("ErrorLoadingData"));
      } finally {
        this.loadingSuperCollections = false;
      }
    },

    async loadCollections() {
      if (!this.storeAddressId) return;

      try {
        this.loadingCollections = true;
        const response = await try_GetCollections(this.storeAddressId);
        // Відповідь може бути масивом або об'єктом з data
        const data = Array.isArray(response) ? response : response?.data || [];
        this.collections = data;
        this.collectionsLoaded = true;
      } catch (error) {
        console.error("Error loading collections:", error);
        this.$toast.error(this.$t("ErrorLoadingData"));
      } finally {
        this.loadingCollections = false;
      }
    },

    async loadProducts() {
      if (!this.storeAddressId) return;

      try {
        this.loadingProducts = true;
        const response = await try_GetProducts(this.storeAddressId);
        // Відповідь може бути масивом або об'єктом з data
        const data = Array.isArray(response) ? response : response?.data || [];
        this.products = data;
        this.filteredProducts = data;
        this.productSearchQuery = "";
        this.productsLoaded = true;
      } catch (error) {
        console.error("Error loading products:", error);
        this.$toast.error(this.$t("ErrorLoadingData"));
      } finally {
        this.loadingProducts = false;
      }
    },

    async loadOptions() {
      if (!this.storeAddressId) return;

      try {
        this.loadingOptions = true;
        const response = await try_GetOptions(this.storeAddressId);
        // Відповідь може бути масивом або об'єктом з data
        const data = Array.isArray(response) ? response : response?.data || [];
        this.optionGroups = data;
        this.optionsLoaded = true;
      } catch (error) {
        console.error("Error loading options:", error);
        this.$toast.error(this.$t("ErrorLoadingData"));
      } finally {
        this.loadingOptions = false;
      }
    },

    filterProducts() {
      const query = this.productSearchQuery.toLowerCase();

      if (!query) {
        this.filteredProducts = this.products;
      } else {
        // Фільтруємо групи, які містять товари з відповідним пошуковим запитом
        this.filteredProducts = this.products
          .map((group) => {
            const filteredItems = group.items.filter(
              (item) =>
                (item.title && item.title.toLowerCase().includes(query)) ||
                (item.description &&
                  item.description.toLowerCase().includes(query))
            );

            // Якщо є відповідні товари або назва групи відповідає запиту
            if (
              filteredItems.length > 0 ||
              group.title.toLowerCase().includes(query)
            ) {
              return {
                ...group,
                items: filteredItems.length > 0 ? filteredItems : group.items,
              };
            }
            return null;
          })
          .filter((group) => group !== null);
      }

      this.currentProductPage = 1;
    },

    confirmDelete(type, item, groupId = null) {
      this.deleteType = type;
      this.itemToDelete = item;
      this.deleteGroupId = groupId;

      const messages = {
        superCollection: this.$t("GlovoConfirmDeleteSuperCollection"),
        collection: this.$t("GlovoConfirmDeleteCollection"),
        product: this.$t("GlovoConfirmDeleteProduct"),
        productGroup: this.$t("GlovoConfirmDeleteProductGroup"),
        optionGroup: this.$t("GlovoConfirmDeleteOptionGroup"),
        option: this.$t("GlovoConfirmDeleteOption"),
      };

      this.deleteModalMessage = messages[type] || this.$t("GlovoConfirmDelete");
      this.showDeleteModal = true;
    },

    async deleteItem() {
      if (!this.itemToDelete) return;

      try {
        let response;

        switch (this.deleteType) {
          case "superCollection":
            response = await try_DeleteSuperCollection(
              this.storeAddressId,
              this.itemToDelete.id
            );
            break;
          case "collection":
            response = await try_DeleteCollection(
              this.storeAddressId,
              this.itemToDelete.id
            );
            break;
          case "product":
            response = await try_DeleteProduct(
              this.storeAddressId,
              this.deleteGroupId,
              this.itemToDelete.id
            );
            break;
          case "productGroup":
            response = await try_DeleteProductGroup(
              this.storeAddressId,
              this.itemToDelete.id
            );
            break;
          case "optionGroup":
            response = await try_DeleteOptionGroup(
              this.storeAddressId,
              this.itemToDelete.id
            );
            break;
          case "option":
            response = await try_DeleteOption(
              this.storeAddressId,
              this.deleteGroupId,
              this.itemToDelete.id
            );
            break;
        }

        if (response && response.status === "success") {
          this.$toast.success(this.$t("GlovoDeleteSuccess"));
          // Перезавантажуємо дані тільки для активного табу
          await this.loadDataForActiveTab();
        } else {
          throw new Error("Failed to delete");
        }
      } catch (error) {
        console.error("Error deleting item:", error);
        this.$toast.error(this.$t("GlovoDeleteError"));
      }
    },

    async clearAllMenu() {
      if (!this.storeAddressId) return;

      try {
        this.showClearAllModal = false;
        this.clearAllConfirmWord = "";

        const response = await try_ClearAllMenu(this.storeAddressId);

        if (response) {
          this.$toast.success(this.$t("GlovoClearAllSuccess"));

          // Скидаємо всі дані та флаги завантаження
          this.superCollections = [];
          this.collections = [];
          this.products = [];
          this.filteredProducts = [];
          this.optionGroups = [];

          this.superCollectionsLoaded = false;
          this.collectionsLoaded = false;
          this.productsLoaded = false;
          this.optionsLoaded = false;

          // Перезавантажуємо дані для активного табу
          await this.loadDataForActiveTab();
        } else {
          throw new Error("Failed to clear all menu");
        }
      } catch (error) {
        console.error("Error clearing all menu:", error);
        this.$toast.error(this.$t("GlovoClearAllError"));
      }
    },

    formatPrice(price) {
      if (!price) return "—";
      return new Intl.NumberFormat("uk-UA", {
        style: "currency",
        currency: "UAH",
      }).format(price / 100);
    },
  },
};
</script>

<style scoped>
.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}

.badge {
  font-size: 0.75rem;
}

/* Ensure confirmation modals appear above parent modal */
::v-deep .modal.show {
  z-index: 1100 !important;
}

::v-deep .modal-backdrop.show {
  z-index: 1090 !important;
}
</style>
